import React, { useState } from 'react';

import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { useUpdateTaxEstimationControl } from '~/hooks/apis/connection-google/useTaxEstimation';

import type { TaxEstimationControl, TaxEstimationMode } from '~/types/taxEstimation';

import { formatTimestamp } from './utils';

export default function TaxEstimationControls({ control }: { control: TaxEstimationControl }) {
  const [mode, setMode] = useState(control.mode);
  const [sampleRate, setSampleRate] = useState(String(control.sampleRate));
  const update = useUpdateTaxEstimationControl();
  const percentage = Number(sampleRate);
  const validSample = sampleRate.trim() !== '' && Number.isInteger(percentage) && percentage >= 0 && percentage <= 100;

  const save = (nextMode: TaxEstimationMode, nextSampleRate: number) =>
    update.mutate({ mode: nextMode, sampleRate: nextSampleRate });

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Production controls</Typography>
      <Typography>
        Saved mode: {control.mode}. Effective mode: {control.effectiveMode}.
      </Typography>
      <Typography variant="body2">
        Changes take effect on new requests within approximately 60 seconds. Scope remains the existing US pilot.
        Sampling controls diagnostic logs only; it does not sample rate application.
      </Typography>
      {control.mode === 'on' && control.effectiveMode === 'shadow' && (
        <Alert severity="info">On is saved, but application is disabled in service config. Running in shadow.</Alert>
      )}
      {control.mode !== 'off' && control.effectiveMode === 'off' && (
        <Alert severity="info">Tax estimation is disabled in service config.</Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="Mode"
          value={mode}
          disabled={update.isPending}
          onChange={(event) => setMode(event.target.value as TaxEstimationMode)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="off">Off</MenuItem>
          <MenuItem value="shadow">Shadow</MenuItem>
          <MenuItem value="on">On</MenuItem>
        </TextField>
        <TextField
          label="Log sample rate (%)"
          type="number"
          value={sampleRate}
          disabled={update.isPending}
          onChange={(event) => setSampleRate(event.target.value)}
          error={!validSample}
          helperText="Whole percentage from 0 to 100"
          inputProps={{ min: 0, max: 100, step: 1 }}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          disabled={update.isPending || !validSample || (mode === control.mode && percentage === control.sampleRate)}
          onClick={() => save(mode, percentage)}
        >
          {update.isPending ? 'Saving…' : 'Save controls'}
        </Button>
      </Stack>
      {update.isError && <Alert severity="error">Unable to save tax estimation controls.</Alert>}
      <Typography variant="body2">
        Last changed by {control.updatedBy} at {formatTimestamp(control.updatedAt)}.
      </Typography>
    </Stack>
  );
}
